// React
import PropTypes from "prop-types";
import { useCallback, useContext, useEffect, useMemo, useState, useRef } from "react";

// @mui material components
import { Card, Fade, Grid, Icon, InputLabel, Modal, Tooltip, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";

import { gql, useLazyQuery, useMutation } from "@apollo/client";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import SoftInputNumber from "components/SoftInputNumberUpgrade";
import CustomPagination from "components/Pagination";
import Loading from "components/Carteles/Loading";
import CartelError from "components/Carteles/Error";
import { MessageManager } from "context";
import { getUproColor } from "utils/colors";
import DataTableCustom from "components/Tables/DataTableCustom";
import HistorialModal from "./HistorialModal";

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    maxHeight: "90%",
    width: "70%",
    overflow: "auto",
};

const QUERY_CLIENTES = gql`
  query getClientes($pagination: PaginationInput, $order: Order, $search: String, $filter: JSON) {
    paginationInfo {
      pages
      total
    }
    clientes(pagination: $pagination, order: $order, search: $search, filter: $filter) {
      id
      nombre
      apellido
      correo
      dni
      esBecado
      habilitadoCuentaCorriente
    }
  }
`;

const MUT_CARGAR_SALDO = gql`
  mutation cargarSaldoCliente($input: CargaSaldoInput!) {
    cargarSaldoCliente(input: $input)
  }
`;

export default function ModalCargaMasiva({ open, handleClose, tipo, idSede }) {
    const { handleSnackbar } = useContext(MessageManager);

    const [page, setPage] = useState(1);
    const [pagesInfo, setPagesInfo] = useState({ pages: 0, total: 0 });
    const [orderBy, setOrderBy] = useState({ campo: "apellido", orden: "ASC" });
    const [search, setSearch] = useState("");
    const [searchTimeout, setSearchTimeout] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [selectAllMatching, setSelectAllMatching] = useState(false);
    const [excludedIds, setExcludedIds] = useState([]);
    const [cantidad, setCantidad] = useState(null);
    const [descripcion, setDescripcion] = useState("");
    const [openHistorial, setOpenHistorial] = useState(false);
    const [openSelectDialog, setOpenSelectDialog] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileError, setFileError] = useState("");
    const inputRef = useRef(null);

    const handleInputFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            setSelectedFile(file);
            if (file.size > 5000000) {
                setFileError("El archivo debe pesar menos de 5MB");
                setSelectedFile(null);
            } else if (
                ![
                    "application/pdf",
                    "image/jpeg",
                    "image/jpg",
                    "image/png",
                ].includes(file.type)
            ) {
                setFileError("El archivo debe ser un PDF, JPG, JPEG o PNG");
                setSelectedFile(null);
            } else {
                setSelectedFile(file);
                setFileError("");
            }
        }
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
        setFileError("");
        if (inputRef.current) inputRef.current.value = "";
    };

    const filtroModal = useMemo(() => {
        if (tipo === "CargarSaldo") {
            return { habilitadoCuentaCorriente: true, _or: [{ esBecado: false }, { esBecado: null }] };
        }
        if (tipo === "BecarCliente") {
            return { habilitadoCuentaCorriente: false, _or: [{ esBecado: false }, { esBecado: null }] };
        }
        return {};
    }, [tipo]);

    const [loadClientes, { data, loading, error }] = useLazyQuery(QUERY_CLIENTES, {
        fetchPolicy: "no-cache",
    });

    const [cargarSaldoCliente, { loading: loadingGuardar }] = useMutation(MUT_CARGAR_SALDO, {
        onCompleted: () => {
            handleSnackbar("Operación realizada correctamente", "success");
            handleClose();
        },
        onError: (err) => {
            handleSnackbar(err?.message || "Error al guardar", "error");
        },
    });

    const fetchData = useCallback(() => {
        if (!open) return;
        loadClientes({
            variables: {
                pagination: { page, limit: 50 },
                order: { field: orderBy.campo, order: orderBy.orden },
                search: search || null,
                filter: filtroModal,
            },
            onCompleted: (res) => {
                setPagesInfo(res?.paginationInfo || { pages: 0, total: 0 });
            },
        });
    }, [open, page, orderBy, search, filtroModal, loadClientes]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (!open) {
            // reset al cerrar
            setPage(1);
            setSelectedIds([]);
            setSelectAllMatching(false);
            setExcludedIds([]);
            setFileError("");
            setCantidad(null);
            setDescripcion("");
            setSelectedFile(null);
            setSearch("");
        }
    }, [open]);

    const handleSearch = (event) => {
        const newTimeout = setTimeout(() => {
            setSearch(event.target.value);
            setPage(1);
        }, 500);
        if (searchTimeout) clearTimeout(searchTimeout);
        setSearchTimeout(newTimeout);
    };

    const rows = data?.clientes || [];
    const idsPagina = rows.map((r) => r.id);
    const isRowChecked = (id) => (selectAllMatching ? !excludedIds.includes(id) : selectedIds.includes(id));
    const selectedOnPageCount = idsPagina.filter((id) => isRowChecked(id)).length;
    const allChecked = rows.length > 0 && selectedOnPageCount === rows.length;
    const someChecked = selectedOnPageCount > 0 && selectedOnPageCount < rows.length;

    const handleSelectVisible = () => {
        const pageIds = rows.map((r) => r.id);
        if (selectAllMatching) {
            // En modo "todos", seleccionar visibles = quitar visibles de excluidos
            setExcludedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
        } else {
            // Agregar visibles a selección explícita, sin duplicados
            setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
        }
        setOpenSelectDialog(false);
    };

    const handleSelectAllMatching = () => {
        setSelectAllMatching(true);
        setExcludedIds([]);
        setSelectedIds([]); // selección explícita ya no se usa en este modo
        setOpenSelectDialog(false);
    };

    const toggleAll = (event) => {
        const pageIds = rows.map((r) => r.id);
        const isSelecting = event?.target?.checked === true || (!allChecked && !someChecked);
        if (allChecked || someChecked) {
            // Acción de deseleccionar visibles
            if (selectAllMatching) {
                // En modo "todos": excluir visibles
                setExcludedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
            } else {
                // En modo normal: quitar visibles de selectedIds
                setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
            }
        } else {
            // Acción de seleccionar
            if (isSelecting) {
                // Mostrar diálogo siempre que se selecciona desde el header
                setOpenSelectDialog(true);
            }
        }
    };

    const toggleOne = (id) => {
        if (selectAllMatching) {
            // En modo "todos": toggle es incluir/excluir individual
            setExcludedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
        } else {
            setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
        }
    };

    const handleGuardar = () => {
        if (!cantidad || Number(cantidad) <= 0) {
            handleSnackbar("El monto debe ser mayor a cero", "warning");
            return;
        }

        const baseInput = {
            tipo,
            total: parseFloat(cantidad),
            descripcion: descripcion || null,
            idSede: idSede || null,
            file: selectedFile || null,
        };

        if (selectAllMatching) {
            cargarSaldoCliente({
                variables: {
                    input: {
                        ...baseInput,
                        selectionByQuery: true,
                        search: search || null,
                        filter: filtroModal || null,
                        excludeIds: excludedIds,
                    },
                },
            });
        } else {
            cargarSaldoCliente({
                variables: {
                    input: {
                        ...baseInput,
                        idClientes: selectedIds,
                    },
                },
            });
        }
    };

    const clear = () => {
        setPage(1);
        setSelectedIds([]);
        setSelectAllMatching(false);
        setExcludedIds([]);
        setFileError("");
        setCantidad(null);
        setDescripcion("");
        setSearch("");
        setSelectedFile(null);
        handleClose();
    }

    // Si cambia búsqueda o filtro y hay selección, deseleccionar todo
    useEffect(() => {
        if (!open) return;
        if (selectAllMatching || selectedIds.length > 0 || excludedIds.length > 0) {
            setSelectAllMatching(false);
            setSelectedIds([]);
            setExcludedIds([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, filtroModal]);

    return (
        <>
            <Modal open={open} onClose={clear} closeAfterTransition>
                <Fade in={open}>
                    <Card sx={style}>
                        <SoftBox>
                            <SoftBox display="flex" justifyContent="space-between" p={3}>
                                <SoftBox>
                                    <SoftTypography variant="h5" fontWeight="medium">
                                        {tipo === "CargarSaldo"
                                            ? "Cargar saldo a cuenta corriente"
                                            : tipo === "BecarCliente"
                                                ? "Becar cliente"
                                                : "Acción masiva"}
                                    </SoftTypography>
                                </SoftBox>

                                <SoftBox display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                                    <Tooltip title="Ver historial de cargas" placement="top">
                                        <SoftButton color="uproBlue" circular iconOnly onClick={() => setOpenHistorial(true)}>
                                            <Icon>history</Icon>
                                        </SoftButton>
                                    </Tooltip>
                                    <Tooltip title="Cerrar" placement="top">
                                        <SoftButton
                                            variant="gradient"
                                            color="secondary"
                                            onClick={handleClose}
                                            circular
                                            fullWidth
                                            iconOnly
                                        >
                                            <Icon>close</Icon>
                                        </SoftButton>
                                    </Tooltip>
                                </SoftBox>
                            </SoftBox>

                            <Grid container spacing={4} px={3} alignItems="flex-start">
                                <Grid item xs={6}>
                                    <SoftBox flex={1}>
                                        <SoftInput
                                            placeholder="Buscar por nombre, apellido o DNI"
                                            icon={{ component: "search", direction: "left" }}
                                            onChange={handleSearch}
                                            fullWidth
                                            size="small"
                                        />
                                    </SoftBox>
                                    <SoftBox flex={1}>
                                        <InputLabel>
                                            <SoftTypography variant="caption" fontWeight="bold" color={getUproColor("dark")}>
                                                Descripción
                                            </SoftTypography>
                                        </InputLabel>
                                        <SoftInput
                                            value={descripcion}
                                            onChange={(e) => setDescripcion(e.target.value)}
                                            placeholder="Descripción"
                                            fullWidth
                                            size="small"
                                        />
                                    </SoftBox>
                                    <SoftBox flex={1}>
                                        <InputLabel>
                                            <SoftTypography variant="caption" fontWeight="bold" color={getUproColor("dark")}>
                                                Monto
                                            </SoftTypography>
                                        </InputLabel>
                                        <SoftInputNumber
                                            value={cantidad}
                                            onChange={(e) => setCantidad(e.target.value)}
                                            placeholder="0.00"
                                            fullWidth
                                            size="small"
                                            name="cantidad"
                                        />
                                    </SoftBox>
                                </Grid>

                                <Grid item xs={6}>
                                    <SoftBox
                                        sx={{
                                            border: selectedFile ? "2px solid #bdbdbd" : "2px dashed #bdbdbd",
                                            borderRadius: 2,
                                            p: 0,
                                            textAlign: "center",
                                            cursor: "pointer",
                                            mb: 1,
                                            backgroundColor: "#fafafa",
                                            transition: "border-color 0.2s",
                                            height: 110,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            "&:hover": {
                                                borderColor: getUproColor("primary"),
                                            },
                                        }}
                                        onDragOver={e => {
                                            if (!selectedFile) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }
                                        }}
                                        onDrop={e => {
                                            if (!selectedFile) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (e.dataTransfer.files && e.dataTransfer.files.length === 1) {
                                                    const file = e.dataTransfer.files[0];
                                                    if (file.size > 5000000) {
                                                        setFileError("El archivo debe pesar menos de 5MB");
                                                        setSelectedFile(null);
                                                    } else if (
                                                        ![
                                                            "application/pdf",
                                                            "image/jpeg",
                                                            "image/jpg",
                                                            "image/png",
                                                        ].includes(file.type)
                                                    ) {
                                                        setFileError("El archivo debe ser un PDF, JPG, JPEG o PNG");
                                                        setSelectedFile(null);
                                                    } else {
                                                        setSelectedFile(file);
                                                        setFileError("");
                                                    }
                                                }
                                            }
                                        }}
                                        onClick={e => {
                                            if (!selectedFile) {
                                                if (inputRef.current) inputRef.current.click();
                                            } else {
                                                const url = URL.createObjectURL(selectedFile);
                                                window.open(url, "_blank", "noopener,noreferrer");
                                                e.stopPropagation();
                                            }
                                        }}
                                    >
                                        {!selectedFile ? (
                                            <SoftBox display="flex" flexDirection="column" alignItems="center" gap={1} width="100%" p={2}>
                                                <Icon sx={{ fontSize: 40, color: getUproColor("primary") }}>cloud_upload</Icon>
                                                <SoftTypography variant="caption" color="textSecondary">
                                                    Hacé click para seleccionar el comprobante (PDF, JPG, PNG) o arrastrá el archivo aquí
                                                </SoftTypography>
                                            </SoftBox>
                                        ) : (
                                            <SoftBox
                                                sx={{
                                                    width: "100%",
                                                    height: "100%",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    cursor: "pointer",
                                                }}
                                            >
                                                {selectedFile.type.startsWith("image/") ? (
                                                    <img
                                                        src={URL.createObjectURL(selectedFile)}
                                                        alt="preview"
                                                        style={{
                                                            width: "auto",
                                                            height: "90%",
                                                            maxWidth: "100%",
                                                            objectFit: "contain",
                                                            borderRadius: 8,
                                                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
                                                        }}
                                                    />
                                                ) : (
                                                    <Icon sx={{ fontSize: 60, color: getUproColor("primary") }}>picture_as_pdf</Icon>
                                                )}
                                            </SoftBox>
                                        )}
                                    </SoftBox>
                                    <input
                                        id="comprobantePagoInput"
                                        ref={inputRef}
                                        type="file"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        style={{ display: "none" }}
                                        onChange={handleInputFileChange}
                                        disabled={!!selectedFile}
                                    />
                                    <SoftBox display="flex" flexDirection="column" alignItems="stretch" gap={1} sx={{ mb: 1 }}>
                                        {selectedFile && (
                                            <>
                                                <SoftTypography
                                                    variant="caption"
                                                    color="primary"
                                                    sx={{ wordBreak: "break-all", textAlign: "center", width: "100%" }}
                                                >
                                                    {selectedFile.name}
                                                </SoftTypography>
                                                <SoftButton
                                                    color="error"
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={handleRemoveFile}
                                                    sx={{ width: "100%" }}
                                                >
                                                    <Icon sx={{ fontSize: 18, mr: 1 }}>delete</Icon>
                                                    Quitar archivo
                                                </SoftButton>
                                            </>
                                        )}
                                        <SoftButton
                                            component="label"
                                            color="primary"
                                            circular
                                            sx={{ width: "100%" }}
                                            disabled={!!selectedFile}
                                            onClick={() => {
                                                if (inputRef.current) inputRef.current.click();
                                            }}
                                        >
                                            <Icon sx={{ mr: 1 }}>upload_file</Icon> Seleccionar archivo
                                        </SoftButton>
                                    </SoftBox>
                                    {fileError && (
                                        <SoftTypography variant="caption" color="error">
                                            {fileError}
                                        </SoftTypography>
                                    )}
                                </Grid>
                            </Grid>

                            <SoftBox mt={1} p={3}>
                                {loading ? (
                                    <Loading />
                                ) : error ? (
                                    <CartelError />
                                ) : (
                                    <DataTableCustom
                                        headerStyles={{
                                            backgroundColor: "#ffffff",             // fondo blanco
                                            borderBottom: "1.5px solid #070606ff",
                                            py: 1,                               // menos alto
                                            "& .MuiTypography-root": {
                                                fontWeight: 600,                      // negrita media
                                                color: "#344767",                     // gris azulado
                                                // textTransform: "none",                // sin uppercase
                                                fontSize: "0.7rem",
                                                letterSpacing: 0.2,
                                            },
                                        }}
                                        columns={[
                                            {
                                                field: "select",
                                                width: "5%",
                                                align: "left",
                                                sortable: false,
                                                renderHeader: () => (
                                                    <Checkbox indeterminate={someChecked} checked={allChecked} onChange={toggleAll} />
                                                ),
                                                renderCell: (row) => (
                                                    <Checkbox checked={isRowChecked(row.id)} onChange={() => toggleOne(row.id)} />
                                                ),
                                            },
                                            {
                                                field: "id",
                                                headerName: "ID",
                                                width: "10%",
                                                align: "left",
                                                renderHeader: (column, isCurrentSort, direction) => (
                                                    <SoftBox
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="flex-start"
                                                        gap={1}
                                                        sx={{
                                                            width: "100%",
                                                            overflow: "hidden",
                                                            minWidth: 0,
                                                        }}
                                                    >
                                                        <SoftBox display="flex" alignItems="center" gap={1}>
                                                            <SoftTypography
                                                                variant="caption"
                                                                fontWeight="bold"
                                                                textTransform="uppercase"
                                                                color="dark"
                                                                sx={{
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "nowrap",
                                                                    flex: 1,
                                                                    minWidth: 0,
                                                                }}
                                                                title={column.headerName}
                                                            >
                                                                {column.headerName}
                                                            </SoftTypography>
                                                        </SoftBox>
                                                        {isCurrentSort && direction ? (
                                                            <Icon
                                                                fontSize="small"
                                                                sx={{
                                                                    color: direction === 'asc' ? 'primary.main' : 'primary.main',
                                                                    opacity: 0.8
                                                                }}
                                                            >
                                                                {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                                                            </Icon>
                                                        ) : (
                                                            <Icon
                                                                fontSize="small"
                                                                sx={{
                                                                    color: 'text.secondary',
                                                                    opacity: 0.4,
                                                                    '&:hover': { opacity: 0.8 }
                                                                }}
                                                            >
                                                                import_export
                                                            </Icon>
                                                        )}
                                                    </SoftBox>
                                                ),
                                                renderCell: (row) => (
                                                    <SoftBox sx={{ width: '100%' }}>
                                                        <SoftTypography variant="button" color="secondary">{row.id}</SoftTypography>
                                                    </SoftBox>
                                                ),
                                            },
                                            {
                                                field: "nombre",
                                                headerName: "Nombre",
                                                // width: "25%",
                                                align: "left",
                                                renderHeader: (column, isCurrentSort, direction) => (
                                                    <SoftBox
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="flex-start"
                                                        gap={1}
                                                        sx={{
                                                            width: "100%",
                                                            overflow: "hidden",
                                                            minWidth: 0,
                                                        }}
                                                    >
                                                        <SoftBox display="flex" alignItems="center" gap={1}>
                                                            <SoftTypography
                                                                variant="caption"
                                                                fontWeight="bold"
                                                                textTransform="uppercase"
                                                                color="dark"
                                                                sx={{
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "nowrap",
                                                                    flex: 1,
                                                                    minWidth: 0,
                                                                }}
                                                                title={column.headerName}
                                                            >
                                                                {column.headerName}
                                                            </SoftTypography>
                                                        </SoftBox>
                                                        {isCurrentSort && direction ? (
                                                            <Icon
                                                                fontSize="small"
                                                                sx={{
                                                                    color: direction === 'asc' ? 'primary.main' : 'primary.main',
                                                                    opacity: 0.8
                                                                }}
                                                            >
                                                                {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                                                            </Icon>
                                                        ) : (
                                                            <Icon
                                                                fontSize="small"
                                                                sx={{
                                                                    color: 'text.secondary',
                                                                    opacity: 0.4,
                                                                    '&:hover': { opacity: 0.8 }
                                                                }}
                                                            >
                                                                import_export
                                                            </Icon>
                                                        )}
                                                    </SoftBox>
                                                ),
                                                renderCell: (row) => (
                                                    <SoftBox sx={{ width: '100%' }}>
                                                        <SoftTypography variant="button" color="secondary">{row.nombre}</SoftTypography>
                                                    </SoftBox>
                                                ),
                                            },
                                            {
                                                field: "apellido",
                                                headerName: "Apellido",
                                                // width: "25%",
                                                align: "left",
                                                renderHeader: (column, isCurrentSort, direction) => (
                                                    <SoftBox
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="flex-start"
                                                        gap={1}
                                                        sx={{
                                                            width: "100%",
                                                            overflow: "hidden",
                                                            minWidth: 0,
                                                        }}
                                                    >
                                                        <SoftBox display="flex" alignItems="center" gap={1}>
                                                            <SoftTypography
                                                                variant="caption"
                                                                fontWeight="bold"
                                                                textTransform="uppercase"
                                                                color="dark"
                                                                sx={{
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "nowrap",
                                                                    flex: 1,
                                                                    minWidth: 0,
                                                                }}
                                                                title={column.headerName}
                                                            >
                                                                {column.headerName}
                                                            </SoftTypography>
                                                        </SoftBox>
                                                        {isCurrentSort && direction ? (
                                                            <Icon
                                                                fontSize="small"
                                                                sx={{
                                                                    color: direction === 'asc' ? 'primary.main' : 'primary.main',
                                                                    opacity: 0.8
                                                                }}
                                                            >
                                                                {direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                                                            </Icon>
                                                        ) : (
                                                            <Icon
                                                                fontSize="small"
                                                                sx={{
                                                                    color: 'text.secondary',
                                                                    opacity: 0.4,
                                                                    '&:hover': { opacity: 0.8 }
                                                                }}
                                                            >
                                                                import_export
                                                            </Icon>
                                                        )}
                                                    </SoftBox>
                                                ),
                                                renderCell: (row) => (
                                                    <SoftBox sx={{ width: '100%' }}>
                                                        <SoftTypography variant="button" color="secondary">{row.apellido}</SoftTypography>
                                                    </SoftBox>
                                                ),
                                            },
                                            {
                                                field: "dni",
                                                headerName: "DNI",
                                                // width: "20%",
                                                align: "left",
                                                sortable: false,
                                                renderHeader: (column, isCurrentSort, direction) => (
                                                    <SoftBox
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="flex-start"
                                                        gap={1}
                                                        sx={{
                                                            width: "100%",
                                                            overflow: "hidden",
                                                            minWidth: 0,
                                                        }}
                                                    >
                                                        <SoftBox display="flex" alignItems="center" gap={1}>
                                                            <SoftTypography
                                                                variant="caption"
                                                                fontWeight="bold"
                                                                textTransform="uppercase"
                                                                color="dark"
                                                                sx={{
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "nowrap",
                                                                    flex: 1,
                                                                    minWidth: 0,
                                                                }}
                                                                title={column.headerName}
                                                            >
                                                                {column.headerName}
                                                            </SoftTypography>
                                                        </SoftBox>
                                                    </SoftBox>
                                                ),
                                                renderCell: (row) => (
                                                    <SoftBox sx={{ width: '100%' }}>
                                                        <SoftTypography variant="button" color="secondary">{row.dni}</SoftTypography>
                                                    </SoftBox>
                                                ),
                                            },
                                            {
                                                field: "correo",
                                                headerName: "Correo",
                                                // width: "30%",
                                                align: "left",
                                                sortable: false,
                                                renderHeader: (column, isCurrentSort, direction) => (
                                                    <SoftBox
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="flex-start"
                                                        gap={1}
                                                        sx={{
                                                            width: "100%",
                                                            overflow: "hidden",
                                                            minWidth: 0,
                                                        }}
                                                    >
                                                        <SoftBox display="flex" alignItems="center" gap={1}>
                                                            <SoftTypography
                                                                variant="caption"
                                                                fontWeight="bold"
                                                                textTransform="uppercase"
                                                                color="dark"
                                                                sx={{
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "nowrap",
                                                                    flex: 1,
                                                                    minWidth: 0,
                                                                }}
                                                                title={column.headerName}
                                                            >
                                                                {column.headerName}
                                                            </SoftTypography>
                                                        </SoftBox>
                                                    </SoftBox>
                                                ),
                                                renderCell: (row) => (
                                                    <SoftBox sx={{ width: '100%' }}>
                                                        <SoftTypography variant="button" color="secondary">{row.correo || "-"}</SoftTypography>
                                                    </SoftBox>
                                                ),
                                            },
                                        ]}
                                        data={rows}
                                        loading={loading}
                                        showActions={false}
                                        maxHeight="40vh"
                                    />
                                )}
                            </SoftBox>

                            <SoftBox mt={1}>
                                <CustomPagination
                                    loading={loading}
                                    length={rows?.length}
                                    total={pagesInfo?.total}
                                    pages={pagesInfo?.pages}
                                    page={page}
                                    setPage={setPage}
                                    noCard
                                />
                            </SoftBox>

                            <SoftBox display="flex" justifyContent="flex-end" alignItems="center" mt={3} p={3}>
                                <SoftButton circular color="dark" onClick={clear} sx={{ mr: 2 }}>
                                    Cancelar
                                </SoftButton>
                                <Tooltip title={"Guardar"} placement="top">
                                    <SoftButton
                                        circular
                                        color="primary"
                                        onClick={handleGuardar}
                                        disabled={!cantidad || loadingGuardar || (!selectAllMatching && selectedIds.length === 0)}
                                    >
                                        {loadingGuardar ? "Guardando..." : "Guardar"}
                                    </SoftButton>
                                </Tooltip>
                            </SoftBox>
                        </SoftBox>
                        <HistorialModal open={openHistorial} handleClose={() => setOpenHistorial(false)} idSede={idSede} />
                    </Card>
                </Fade>
            </Modal>
            {/* Dialog de selección masiva */}
            <Dialog open={openSelectDialog} sx={{ p: 2 }} onClose={() => setOpenSelectDialog(false)}>
                <DialogTitle>Seleccionar</DialogTitle>
                <DialogContent>
                    <SoftTypography variant="button" color="secondary">
                        ¿Qué querés seleccionar?
                    </SoftTypography>
                    <SoftTypography variant="caption" display="block" sx={{ mt: 1 }}>
                        Visible: {rows.length}
                    </SoftTypography>
                    <SoftTypography variant="caption" display="block">
                        Todos los resultados: {pagesInfo?.total || 0}
                    </SoftTypography>
                </DialogContent>
                <DialogActions>
                    <SoftButton color="secondary" onClick={handleSelectVisible}>
                        Seleccionar visible
                    </SoftButton>
                    <SoftButton color="primary" onClick={handleSelectAllMatching}>
                        Seleccionar todo
                    </SoftButton>
                </DialogActions>
            </Dialog>
        </>
    );
}

ModalCargaMasiva.propTypes = {
    open: PropTypes.bool,
    handleClose: PropTypes.func,
    tipo: PropTypes.oneOf(["CargarSaldo", "BecarCliente"]).isRequired,
    idSede: PropTypes.number,
};

// Historial anidado
export { HistorialModal };
